// Trustline validation service
import { trustline, TrustlineInitOptions, TrustlineWeb3ValidateParams, TrustlineValidateResponse, ConfigurePolicyParams, ConfigurePolicyResult, FetchPolicyParams, FetchPolicyResult, FetchDefaultPolicyParams, FetchDefaultPolicyResult } from '@trustline.id/websdk';

class TrustlineService {
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    try {
      const initOptions: TrustlineInitOptions = {
        clientId: process.env.REACT_APP_TRUSTLINE_CLIENT_ID || 'YOUR_TRUSTLINE_CLIENT_ID',
        loginUri: process.env.REACT_APP_TRUSTLINE_LOGIN_URI || 'https://yourapp.com/auth/trustline/callback'
      };
      trustline.init(initOptions);
      this.isInitialized = true;
      console.log('Trustline SDK initialized');
    } catch (error) {
      console.error('Failed to initialize Trustline SDK:', error);
    }
  }

  async validateTransaction(data: TrustlineWeb3ValidateParams): Promise<TrustlineValidateResponse> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }

    try {
      const response = await trustline.validate(data);
      return response;
    } catch (error) {
      console.error('Trustline validation failed:', error);
      throw error;
    }
  }

  private async validateAndCheckStatus(
    validationData: TrustlineWeb3ValidateParams,
    operationName: string
  ): Promise<boolean> {
    try {
      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error(`Trustline validation error for ${operationName}:`, response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log(`${operationName} transaction approved by Trustline`);
          return true;
        case 'approval_required':
          console.log(`${operationName} transaction requires manual approval`);
          return false;
        case 'rejected':
          console.log(`${operationName} transaction rejected by Trustline:`, response.result.reason);
          return false;
        default:
          console.log(`Unknown validation status for ${operationName}:`, (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error(`Trustline validation failed for ${operationName}:`, error);
      return false;
    }
  }

  /**
   * Validate ERC4626 deposit operation
   */
  async validateVaultDeposit(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    assetAddress: string,
    assets: string,
    receiver: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: assetAddress === '0x0000000000000000000000000000000000000000' ? assets : '0',
      data: {
        functionPrototype: 'deposit(uint256,address)',
        args: [assets, receiver]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Deposit');
  }

  /**
   * Validate ERC4626 mint operation
   */
  async validateVaultMint(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    assetAddress: string,
    shares: string,
    receiver: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: assetAddress === '0x0000000000000000000000000000000000000000' ? '0' : '0',
      data: {
        functionPrototype: 'mint(uint256,address)',
        args: [shares, receiver]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Mint');
  }

  /**
   * Validate ERC4626 withdraw operation
   */
  async validateVaultWithdraw(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    assets: string,
    receiver: string,
    owner: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'withdraw(uint256,address,address)',
        args: [assets, receiver, owner]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Withdraw');
  }

  /**
   * Validate ERC4626 redeem operation
   */
  async validateVaultRedeem(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    shares: string,
    receiver: string,
    owner: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'redeem(uint256,address,address)',
        args: [shares, receiver, owner]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Redeem');
  }

  /**
   * Validate setFrozen operation
   */
  async validateSetFrozen(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    frozen: boolean
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'setFrozen(bool)',
        args: [frozen.toString()]
      }
    };

    return this.validateAndCheckStatus(validationData, 'SetFrozen');
  }

  /**
   * Validate adminWithdraw operation
   */
  async validateAdminWithdraw(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    to: string,
    assets: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'adminWithdraw(address,uint256)',
        args: [to, assets]
      }
    };

    return this.validateAndCheckStatus(validationData, 'AdminWithdraw');
  }

  /**
   * Validate adminDeposit operation
   */
  async validateAdminDeposit(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    from: string,
    assets: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'adminDeposit(address,uint256)',
        args: [from, assets]
      }
    };

    return this.validateAndCheckStatus(validationData, 'AdminDeposit');
  }

  /**
   * Validate adminWithdrawToHyperCore operation
   */
  async validateAdminWithdrawToHyperCore(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    assets: string,
    destinationDex: number
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'adminWithdrawToHyperCore(uint256,uint32)',
        args: [assets, destinationDex.toString()]
      }
    };

    return this.validateAndCheckStatus(validationData, 'AdminWithdrawToHyperCore');
  }

  /**
   * Validate setTrustlinePolicy operation
   * @param trustlineOnAdminWithdrawToHyperCore Only used on HyperEVM networks (chainId 999 or 998)
   */
  async validateSetTrustlinePolicy(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    trustlineOnDeposit: boolean,
    trustlineOnMint: boolean,
    trustlineOnWithdraw: boolean,
    trustlineOnRedeem: boolean,
    trustlineOnTransfer: boolean,
    trustlineOnSetFrozen: boolean,
    trustlineOnAdminWithdraw: boolean,
    trustlineOnAdminDeposit: boolean,
    trustlineOnAdminWithdrawToHyperCore?: boolean
  ): Promise<boolean> {
    // Check if we're on HyperEVM (chainId 999 or 998)
    const isHyperEVM = chainId === 999 || chainId === 998;
    
    // Build args array - include trustlineOnAdminWithdrawToHyperCore only on HyperEVM
    const args: string[] = [
      trustlineOnDeposit.toString(),
      trustlineOnMint.toString(),
      trustlineOnWithdraw.toString(),
      trustlineOnRedeem.toString(),
      trustlineOnTransfer.toString(),
      trustlineOnSetFrozen.toString(),
      trustlineOnAdminWithdraw.toString(),
      trustlineOnAdminDeposit.toString()
    ];

    if (isHyperEVM) {
      // On HyperEVM, include the 9th parameter
      args.push((trustlineOnAdminWithdrawToHyperCore ?? false).toString());
    }

    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: isHyperEVM 
          ? 'setTrustlinePolicy(bool,bool,bool,bool,bool,bool,bool,bool,bool)'
          : 'setTrustlinePolicy(bool,bool,bool,bool,bool,bool,bool,bool)',
        args
      }
    };

    return this.validateAndCheckStatus(validationData, 'SetTrustlinePolicy');
  }

  // Legacy methods kept for backward compatibility
  async validateDeposit(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    tokenAddress: string,
    amount: string,
    beneficiary: string
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: tokenAddress === '0x0000000000000000000000000000000000000000' ? amount : '0',
      data: {
        functionPrototype: 'depose(address,address,uint256)',
        args: [tokenAddress, beneficiary, amount]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Deposit');
  }

  // Legacy methods kept for backward compatibility
  async validateWithdraw(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'withdraw(uint256)',
        args: [depositId.toString()]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Withdraw');
  }

  async validateRefund(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    const validationData: TrustlineWeb3ValidateParams = {
      chainId: chainId.toString(),
      senderAddress,
      contractAddress,
      nativeAmount: '0',
      data: {
        functionPrototype: 'refund(uint256)',
        args: [depositId.toString()]
      }
    };

    return this.validateAndCheckStatus(validationData, 'Refund');
  }

  async configurePolicy(
    params: ConfigurePolicyParams,
    eip712Signer: (domain: any, types: any, message: any) => Promise<string>
  ): Promise<ConfigurePolicyResult> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }
    
    try {
      const response = await trustline.configurePolicy(params, eip712Signer);
      return response;
    } catch (error) {
      console.error('Trustline configurePolicy failed:', error);
      throw error;
    }
  }

  /*async configurePolicyForWithdraw(
    chainId: number,
    senderAddress: string, // The beneficiary who will withdraw
    contractAddress: string,
    depositId: number,
    allowedEmailDomains: string[],
    signer: ethers.Signer
  ): Promise<ConfigurePolicyResult> {
    try {
      // Get signer address
      const signerAddress = await signer.getAddress();
      
      // Create EIP-712 signer function
      const eip712Signer = async (domain: any, types: any, message: any) => {
        return await signer.signTypedData(domain, types, message);
      };

      const params: ConfigurePolicyParams = {
        chainId: chainId.toString(),
        senderAddress: senderAddress,
        signerAddress: signerAddress,
        contractAddress: contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'withdraw(uint256)',
          args: [depositId.toString()]
        },
        policyType: 'UserAuthenticationPolicy',
        customization: {
          allowedEmailDomains: allowedEmailDomains.map(domain => domain.toLowerCase()),
          requireEmailVerification: true
        }
      };

      const response = await this.configurePolicy(params, eip712Signer);
      return response;
    } catch (error) {
      console.error('Trustline configurePolicyForWithdraw failed:', error);
      throw error;
    }
  }*/

  async fetchPolicy(
    params: FetchPolicyParams
  ): Promise<FetchPolicyResult> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }
    
    try {
      const response = await trustline.fetchPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchPolicy failed:', error);
      throw error;
    }
  }

  /*async fetchPolicyForWithdraw(
    chainId: number,
    senderAddress: string, // The beneficiary who will withdraw
    contractAddress: string,
    depositId: number
  ): Promise<FetchPolicyResult> {
    try {
      const params: FetchPolicyParams = {
        chainId: chainId.toString(),
        senderAddress: senderAddress,
        contractAddress: contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'withdraw(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.fetchPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchPolicyForWithdraw failed:', error);
      throw error;
    }
  }*/

  async fetchDefaultPolicy(
    params: FetchDefaultPolicyParams
  ): Promise<FetchDefaultPolicyResult> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }
    
    try {
      const response = await trustline.fetchDefaultPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchDefaultPolicy failed:', error);
      throw error;
    }
  }

  async fetchDefaultPolicyForWithdraw(
    chainId: number,
    contractAddress: string
  ): Promise<FetchDefaultPolicyResult> {
    try {
      const params: FetchDefaultPolicyParams = {
        chainId: chainId.toString(),
        contractAddress: contractAddress,
        data: {
          functionPrototype: 'withdraw(uint256,address,address)',
          args: ['0', '0x0000000000000000000000000000000000000000', '0x0000000000000000000000000000000000000000'] // Use placeholder values for default policy lookup
        }
      };

      const response = await this.fetchDefaultPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchDefaultPolicyForWithdraw failed:', error);
      throw error;
    }
  }

  async fetchDefaultPolicyForAdminWithdraw(
    chainId: number,
    contractAddress: string
  ): Promise<FetchDefaultPolicyResult> {
    try {
      const params: FetchDefaultPolicyParams = {
        chainId: chainId.toString(),
        contractAddress: contractAddress,
        data: {
          functionPrototype: 'adminWithdraw(address,uint256)',
          args: ['0x0000000000000000000000000000000000000000', '0'] // Use placeholder values for default policy lookup
        }
      };

      const response = await this.fetchDefaultPolicy(params);
      return response;
    } catch (error) {
      console.error('Trustline fetchDefaultPolicyForAdminWithdraw failed:', error);
      throw error;
    }
  }
}

export const trustlineService = new TrustlineService();
